import React, { FC, useState } from 'react'
import clsx from 'clsx'

import makeStyles from '@material-ui/core/styles/makeStyles'
import { ListItem, Button, Collapse, colors, Theme, SvgIconTypeMap } from '@material-ui/core'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'

import { useTranslation } from 'react-i18next'
import useRouter from '../../../helpers/useRouter'
import { OverridableComponent } from '@material-ui/core/OverridableComponent'
import { CSSProperties } from '@material-ui/core/styles/withStyles'

const useStyles = makeStyles((theme: Theme) => ({
    item: {
        display: 'block',
        paddingTop: 0,
        paddingBottom: 0,
    },
    itemLeaf: {
        display: 'flex',
        paddingTop: 0,
        paddingBottom: 0,
        '&:hover': {
            backgroundColor: theme.palette.acolad.lightBlue,
        },
    },
    itemLeafSelected: {
        backgroundColor: theme.palette.acolad.lightBlue,
    },
    button: {
        color: colors.blueGrey[800],
        padding: '10px 8px',
        justifyContent: 'flex-start',
        textTransform: 'none',
        letterSpacing: 0,
        width: '100%',
    },
    buttonLeaf: {
        color: colors.blueGrey[800],
        padding: '10px 8px',
        justifyContent: 'flex-start',
        textTransform: 'none',
        letterSpacing: 0,
        width: '100%',
        fontWeight: theme.typography.fontWeightRegular,
        '&.depth-0': {
            fontWeight: theme.typography.fontWeightMedium,
        },
    } as CSSProperties,
    icon: {
        color: theme.palette.icon,
        display: 'flex',
        alignItems: 'center',
        marginRight: theme.spacing(1),
    },
    expandIcon: {
        marginLeft: 'auto',
        height: 16,
        width: 16,
    },
    label: {
        display: 'flex',
        alignItems: 'center',
        marginLeft: 'auto',
    },
}))

interface Props {
    className?: string
    depth: number
    href?: string
    icon: OverridableComponent<SvgIconTypeMap<any, 'svg'>>
    label: any
    open?: boolean
    title: string
    onClose?: () => void
    children?: React.ReactNode
}

const NavigationListItem: FC<Props> = ({
    title,
    href,
    depth = 0,
    children,
    icon: Icon = undefined,
    className = '',
    open = false,
    label: Label = '',
    onClose = undefined,
}) => {
    const classes = useStyles()
    const router = useRouter()

    const [openState, setOpenState] = useState<boolean>(open)

    const { t } = useTranslation()

    const handleToggle = () => {
        setOpenState((op) => !op)
    }

    const handleClick = () => {
        router.history.push(href)
        onClose()
    }

    let paddingLeft = 8

    if (depth > 0) {
        paddingLeft = 32 + 8 * depth
    }

    const style = {
        paddingLeft,
    }

    if (children) {
        return (
            <ListItem disableGutters className={clsx(classes.item, className)}>
                <Button className={classes.button} onClick={handleToggle} style={style}>
                    {Icon && <Icon className={classes.icon} />}
                    {t(title)}
                    {openState ? (
                        <ExpandLessIcon className={classes.expandIcon} color="inherit" />
                    ) : (
                        <ExpandMoreIcon className={classes.expandIcon} color="inherit" />
                    )}
                </Button>
                <Collapse in={openState}>{children}</Collapse>
            </ListItem>
        )
    }
    return (
        <ListItem
            disableGutters
            className={clsx(href === router.location.pathname ? classes.itemLeafSelected : classes.itemLeaf, className)}
        >
            <Button className={clsx(classes.buttonLeaf, `depth-${depth}`)} onClick={handleClick} style={style}>
                {Icon && <Icon className={classes.icon} />}
                {t(title)}
                {Label && (
                    <span className={classes.label}>
                        <Label />
                    </span>
                )}
            </Button>
        </ListItem>
    )
}

export default NavigationListItem
